/**
 * La sesión: puerta de acceso y sincronización del token.
 *
 * Hay rutas que no piden identidad y no es un descuido: la portada, el test
 * gratuito y el informe compartido por enlace tienen que funcionar sin cuenta.
 * El test gratuito es la puerta de entrada al producto, y exigir registro antes
 * de dar nada es justo lo que hunde la conversión.
 */

import { useEffect, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";

import { Logo } from "./Logo";
import { setToken } from "../lib/auth";

/**
 * Mantiene el token al día para el cliente de la API.
 *
 * Se escribe en un módulo y no en un contexto de React porque quien lo consume
 * son funciones sueltas de `fetch`, fuera del árbol de componentes.
 */
export function SincronizarToken({ children }: { children: ReactNode }) {
  const auth = useAuth();
  // Durante el render, no en un efecto: React ejecuta los efectos de los hijos
  // ANTES que los del padre, asi que un efecto aqui llegaria tarde. La pantalla
  // que abre sesion lanza su peticion en su propio efecto, y salia sin token:
  // 401, mientras la misma llamada hecha a mano funcionaba.
  setToken(auth.user?.access_token ?? null);
  return <>{children}</>;
}

function Marco({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Cómo mandar a alguien a Keycloak.
 *
 * Dos casos distintos, y confundirlos cuesta altas:
 *
 * **Viene del formulario de critero.es** (`?email=`). Acaba de escribir su
 * nombre y su correo para empezar el diagnóstico: no tiene cuenta. Va derecho
 * al formulario de registro — `prompt=create`, del estándar OIDC y que Keycloak
 * entiende — con el correo ya puesto. Mandarlo al de acceso le obligaría a
 * encontrar el enlace de «Registrarse» y a teclear el correo otra vez.
 *
 * **Llega por su cuenta.** Puede tener cuenta o no, así que va al acceso, que
 * ya ofrece registrarse.
 */
function irAKeycloak(auth: ReturnType<typeof useAuth>) {
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email");

  void auth.signinRedirect({
    // A dónde iba antes de que le pidiéramos identificarse. Sin esto, quien
    // pulsa "Empezar el diagnóstico" vuelve del registro a la lista de
    // resultados — vacía, porque acaba de crearse la cuenta — en vez de al
    // cuestionario que había pedido.
    //
    // Viaja en el `state` de OIDC, que es justo para esto: el servidor lo
    // devuelve intacto y no puede manipularlo nadie por el camino.
    state: { destino: window.location.pathname + window.location.search },
    ...(email
      ? { extraQueryParams: { prompt: "create", login_hint: email } }
      : {}),
  });
}

/**
 * A dónde volver tras identificarse.
 *
 * Solo rutas de esta misma aplicación. Un `state` con `https://otro-sitio` o
 * con `//otro-sitio` — que el navegador lee como protocolo relativo — convertiría
 * el acceso en un trampolín para llevar a alguien a otra parte justo después de
 * escribir su contraseña, que es el mejor momento para engañarle.
 */
export function destinoSeguro(estado: unknown): string {
  const d = (estado as { destino?: unknown } | undefined)?.destino;
  if (typeof d !== "string" || !d.startsWith("/") || d.startsWith("//")) {
    return "/resultados";
  }
  return d;
}

/**
 * La raiz de `app.critero.es`.
 *
 * Aqui llega quien quiere entrar en su cuenta, y hasta ahora se encontraba una
 * landing: la que tenia sentido cuando la aplicacion era todo lo que habia. El
 * sitio comercial vive desde septiembre en `critero.es`, asi que esa portada
 * duplicaba contenido, con otro diseno, y dejaba a quien venia a identificarse
 * buscando el boton.
 *
 * Con sesion se entra directo a los resultados. Sin ella se va al formulario de
 * Keycloak sin pantalla intermedia — ahi tambien se puede crear cuenta, que es
 * la otra razon por la que alguien teclea esta direccion.
 *
 * Ya no hay test corto que ofrecer: el diagnostico completo es lo gratuito, y
 * para guardarlo hace falta cuenta. Quien llega aqui viene justamente a eso.
 */
export function Entrada() {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator) {
      irAKeycloak(auth);
    }
  }, [auth]);

  if (auth.isAuthenticated) return <Navigate to="/resultados" replace />;

  if (auth.error) {
    return (
      <Marco>
        <p className="mb-6 text-ink/70">No se ha podido comprobar tu sesión.</p>
        <button
          type="button"
          onClick={() => void auth.signinRedirect()}
          className="critero-boton"
        >
          Volver a intentarlo
        </button>
      </Marco>
    );
  }

  return <LlevandoAlAcceso />;
}

/**
 * El paso por Keycloak, mientras ocurre.
 *
 * Se ve un instante y a veces nada, según lo que tarde la redirección. Dice a
 * dónde va para que ese parpadeo no parezca un error.
 */
function LlevandoAlAcceso() {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator) {
      irAKeycloak(auth);
    }
  }, [auth]);

  return <Marco>Llevándote al acceso…</Marco>;
}

/** Envuelve lo que exige tener cuenta. */
export function Protegido({ children }: { children: ReactNode }) {
  const auth = useAuth();

  // Mientras se resuelve la sesión no se decide nada: pintar el acceso aquí
  // haría parpadear una pantalla de login a quien ya está dentro.
  if (auth.isLoading) {
    return <Marco>Comprobando tu sesión…</Marco>;
  }

  if (auth.error) {
    return (
      <Marco>
        <p className="mb-6 text-ink/70">No se ha podido comprobar tu sesión.</p>
        <button
          type="button"
          onClick={() => void auth.signinRedirect()}
          className="critero-boton"
        >
          Volver a intentarlo
        </button>
      </Marco>
    );
  }

  if (!auth.isAuthenticated) {
    // Sin pantalla intermedia. Quien acaba de pulsar "Empezar el diagnóstico"
    // en critero.es ya ha dicho que quiere entrar: una pantalla que solo repite
    // la pregunta con otro botón es un clic de más entre él y el producto, y
    // además era la única que quedaba con el diseño anterior.
    return <LlevandoAlAcceso />;
  }

  return <>{children}</>;
}

/** Aterrizaje tras volver de Keycloak. */
export function VueltaDelAcceso() {
  const auth = useAuth();

  useEffect(() => {
    if (auth.isAuthenticated) {
      window.location.replace(destinoSeguro(auth.user?.state));
    }
  }, [auth.isAuthenticated, auth.user]);

  if (auth.error) {
    return (
      <Marco>
        <p className="mb-6 text-ink/70">No se ha podido completar el acceso.</p>
        <a href="/" className="text-teal underline underline-offset-4">
          Volver al principio
        </a>
      </Marco>
    );
  }
  return <Marco>Entrando…</Marco>;
}

/** Barra de identidad, para las pantallas con sesión. */
export function BarraSesion() {
  const auth = useAuth();
  if (!auth.isAuthenticated) return null;

  const quien =
    (auth.user?.profile.name as string) ?? (auth.user?.profile.email as string) ?? "Tu cuenta";

  return (
    <span className="flex items-center gap-3 text-sm text-ink/60">
      <span className="max-w-40 truncate">{quien}</span>
      <button
        type="button"
        onClick={() => void auth.signoutRedirect()}
        className="text-teal underline underline-offset-4"
      >
        Salir
      </button>
    </span>
  );
}
